import React from "react";
import {
  Edit,
  Trash2,
  Tag,
  Calendar,
  FileText,
  ArrowDownCircle,
  MinusCircle,
  ArrowUpCircle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Modal from "../common/Modal";
import { useLanguage } from "../../i18n/LanguageContext";

const renderPriority = (priority, t) => {
  let icon;
  let colorClass;
  let text;

  switch (priority) {
    case "Low":
      icon = <ArrowDownCircle className="h-4 w-4 mr-1 text-green-500" />;
      colorClass = "text-green-700";
      text = t("goal.lowPriority");
      break;
    case "Medium":
      icon = <MinusCircle className="h-4 w-4 mr-1 text-yellow-500" />;
      colorClass = "text-yellow-700";
      text = t("goal.mediumPriority");
      break;
    case "High":
      icon = <ArrowUpCircle className="h-4 w-4 mr-1 text-red-500" />;
      colorClass = "text-red-700";
      text = t("goal.highPriority");
      break;
    default:
      return null;
  }

  return (
    <span className={`flex items-center text-sm font-semibold ${colorClass}`}>
      {icon}
      {text}
    </span>
  );
};

const GoalDetail = ({ goal, onClose, onEdit, onDelete }) => {
  const { t } = useLanguage();
  if (!goal) {
    return null;
  }

  return (
    <Modal title={t("goal.details")} onClose={onClose}>
      <div className="p-4 flex flex-col h-full">
        <div className="pb-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-2xl font-bold leading-6 text-gray-900 break-words">
            {goal.title}
          </h3>
        </div>

        <div className="flex items-center space-x-6 mt-4 text-sm text-gray-700 flex-wrap gap-y-2 flex-shrink-0">
          {goal.category && (
            <span className="inline-flex items-center">
              <Tag className="h-4 w-4 mr-1 text-purple-500" /> {goal.category}
            </span>
          )}
          {goal.type && (
            <span className="inline-flex items-center">
              {t("goal.type")}: {goal.type === "Recurring" ? t("goal.recurring") : t("goal.oneTime")}
            </span>
          )}
          {goal.priority && renderPriority(goal.priority, t)}
          {goal.targetDate && (
            <span className="inline-flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-gray-500" /> {t("goal.targetDate")}: {" "}
              {new Date(goal.targetDate).toLocaleDateString()}
            </span>
          )}
        </div>

        {goal.progress !== undefined && (
          <div className="mt-4 flex-shrink-0">
            <div className="flex items-center mb-1 text-gray-700">
              <span className="font-medium text-sm">
                {t("goal.progress")}: {goal.progress}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${goal.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {goal.description && (
          <div className="mt-4 text-gray-700 leading-relaxed overflow-y-auto flex-grow pr-2 min-h-[100px]">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {t("goal.description")}
            </h4>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {goal.description}
            </ReactMarkdown>
          </div>
        )}

        {goal.journal_entries && goal.journal_entries.length > 0 && (
          <div className="mt-4 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <div className="mb-3 flex items-center gap-2 text-gray-900">
              <FileText className="h-4 w-4 text-purple-500" />
              <h4 className="text-sm font-semibold">{t("goal.linkedEntries")}</h4>
            </div>
            <div className="space-y-2">
              {goal.journal_entries.map((entry) => (
                <div key={entry.id} className="rounded-xl bg-white px-3 py-2 text-sm shadow-sm">
                  <p className="font-medium text-gray-900">{entry.title}</p>
                  <p className="text-xs text-gray-500">
                    {entry.date ? new Date(entry.date).toLocaleDateString() : t("common.noDate")}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex justify-end space-x-3 pt-4 border-t border-gray-200 flex-shrink-0">
          <button
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            onClick={() => onEdit(goal)}
          >
            <Edit
              className="-ml-1 mr-2 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
            {t("common.edit")}
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            onClick={() => onDelete(goal.id)}
          >
            <Trash2 className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            {t("common.delete")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GoalDetail;
